import { withStyles } from '@material-ui/core/styles';
import * as _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';

const styles = (theme) => ({
  galleryContainer: {
    height: 200,
    marginTop: 20,
    background: theme.colors.white,
    borderRadius: 0,
    display: 'flex',
    position: 'relative',
    fontFamily: 'Sofia-pro, sans-serif',
    margin: '0 24px',
  },
  mainImageContainer: {
    position: 'relative',
    flex: 3,
  },
  mainImage: {
    height: '100%',
    borderRadius: 12,
    objectFit: 'cover',    
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  imageElement: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: 12,
  },
  thumbsColumn: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 18,    
    gap: '18px',
    flex: 1,
    height: '100%',
    overflow: 'hidden',
  },
  thumbContainer: {
    position: 'relative',
    height: 81,
    flex: 1,
    minHeight: 0,
  },
  thumb: {
    height: '100%',
    width: '100%',
    borderRadius: 12,
    objectFit: 'cover',
    position: 'relative',
    cursor: 'pointer',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: 81,
    borderRadius: 12,
    background: 'rgba(0,0,0,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: 18,
    pointerEvents: 'none',
  },
  viewGalleryButton: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 111,
    height: 34,
    borderRadius: 17,
    background: 'transparent',
    color: '#FFFFFF',
    border: '1px solid #FFFFFF',
    fontFamily: 'Sofia-pro, sans-serif',
    fontWeight: 500,
    fontSize: 16,
    cursor: 'pointer',
    zIndex: 2,
  },
  title: {
    position: 'absolute',
    left: 47,
    bottom: 20,
    color: '#FFFFFF',
    fontFamily: 'Abuget',
    fontWeight: 'normal',
    fontSize: 84,
    lineHeight: '48px',
    background: 'none',
    borderRadius: 0,
    padding: 0,
    boxSizing: 'border-box',
    textShadow: '0 2px 8px rgba(0,0,0,0.10)',
  },
});

const ClientItineraryGallery = ({
  openGallery,
  itineraryData,
  classes,
}) => {
  // Store shuffled images in ref to persist across re-renders - shuffle only once per page load
  const shuffledImagesRef = useRef(null);
  
  // Get banner images from API response - shuffle only once per page load
  if (!shuffledImagesRef.current && itineraryData?.itineraryParts) {
    const images = itineraryData.itineraryParts
      .filter((part) => !part?.activityData?.isTransfer)
      .map((part) => part?.stayData?.images?.[0] || part?.activityData?.images?.[0])
      .filter(Boolean);
    shuffledImagesRef.current = _.shuffle(images);
  }
  
  const bannerImages = shuffledImagesRef.current || [];

  // Generate dynamic title
  const tripTitle = itineraryData?.title || 'Your Tailored Vacation';

  return (
    <div className={classes.galleryContainer}>
      <div className={classes.mainImageContainer}>
        <div
          role="button"
          tabIndex={0}
          className={classes.mainImage}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            if (openGallery) {
              openGallery(bannerImages, 0);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (openGallery) {
                openGallery(bannerImages, 0);
              }
            }
          }}
        >
          <img
            src={bannerImages[0]}
            alt="Main"
            className={classes.imageElement}
          />
          <div className={classes.title}>{tripTitle}</div>
        </div>
      </div>
      <div className={classes.thumbsColumn}>
        {bannerImages[1] && (
          <div className={classes.thumbContainer}>
            <div
              role="button"
              tabIndex={0}
              className={classes.thumb}
              onClick={() => {
                if (openGallery) {
                  openGallery(bannerImages, 1);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (openGallery) {
                    openGallery(bannerImages, 1);
                  }
                }
              }}
            >
              <img
                src={bannerImages[1]}
                alt="Thumb1"
                className={classes.imageElement}
              />
            </div>
          </div>
        )}
        {bannerImages[2] && (
          <div className={classes.thumbContainer}>
            <div
              role="button"
              tabIndex={0}
              className={classes.thumb}
              onClick={() => {
                if (openGallery) {
                  openGallery(bannerImages, 2);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (openGallery) {
                    openGallery(bannerImages, 2);
                  }
                }
              }}
            >
              <img
                src={bannerImages[2]}
                alt="Thumb2"
                className={classes.imageElement}
              />
            </div>
            <div className={classes.overlay} />
            <button
              type="button"
              className={classes.viewGalleryButton}
              onClick={() => {
                if (openGallery) {
                  openGallery(bannerImages, 0);
                }
              }}
            >
              View Gallery
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

ClientItineraryGallery.propTypes = {
  openGallery: PropTypes.func.isRequired,
  itineraryData: PropTypes.shape({
    title: PropTypes.string,
    itineraryParts: PropTypes.arrayOf(PropTypes.shape({
      stayData: PropTypes.shape({
        images: PropTypes.arrayOf(PropTypes.string),
      }),
      activityData: PropTypes.shape({
        images: PropTypes.arrayOf(PropTypes.string),
      }),
    })),
  }),
  isChatMinimized: PropTypes.bool.isRequired,
  classes: PropTypes.object.isRequired,
};

ClientItineraryGallery.defaultProps = {
  itineraryData: null,
};

export default withStyles(styles)(ClientItineraryGallery);
